import React, { useState, useEffect, useMemo, useCallback } from 'react';
import i18next from 'i18next';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDictionaryHandling, useParams, useSimpleError, useQueryWithSimpleErrorHandling } from 'hooks';
import { useMutation } from 'features/common/security/hooks';
import { MONTH_NO_VALUES_QUERY } from '../queries';
import {
  CURRENCY_QUERY,
  INSURANCE_TYPES_QUERY,
  ACTIVITY_TYPES_QUERY,
  FINANCING_TYPES_QUERY
} from 'features/insuranceDocument/queries/insuranceDocumentQueries';
import { SAVE_INSURANCE_DATA } from '../mutations';
import { INSURANCE_DOCUMENT_UPDATED } from 'features/insuranceDocument/subscriptions/insuranceDocumentSubscriptions';
import { useSubscription } from '@apollo/client';
import { useChangeTrackingLens, get, set, sequence } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import {
  items,
  shape,
  required,
  when,
  Failure,
  ValidationError,
  Success,
  Validator,
  stopOnFirstFailure,
  between
} from '@totalsoft/pure-validations';
import { isPositiveOrZero, dateIsGreaterThanOrEqualToCurrentDate } from 'utils/simple-validations/validations';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { emptyArray, emptyString } from 'constants/defaultValues';
import { getActivityTypes } from 'features/insuranceDocument/edit/utils';
import { Prompt } from 'react-router-dom';
import InsuranceDataCard from './InsuranceDataCard';

const validatorInsurers = Validator(function isSelected(content) {
  return content.filter(item => item.selected).length !== 0
    ? Success
    : Failure(ValidationError(i18next.t('Validations.InsuranceData.AtLeastOneInsurer')));
});

const validatorPeriods = Validator(function isSelected(content) {
  return content.filter(item => item.selected).length !== 0
    ? Success
    : Failure(ValidationError(i18next.t('Validations.InsuranceData.AtLeastOnePeriod')));
});

const getInsuranceDataValidator = (insuranceTypeCode, cascoInsuranceTypeCode, rcaInsuranceTypeCode, hasExternalBeneficiary) => {
  return shape({
    financingType: required,
    monthNo: required,
    startDate: [required, dateIsGreaterThanOrEqualToCurrentDate] |> stopOnFirstFailure,
    currencyId: required |> when(insuranceTypeCode === cascoInsuranceTypeCode),
    insurers:
      [
        validatorInsurers,
        items(
          shape({
            requestedCommissionPercent:
              [isPositiveOrZero |> when(insuranceTypeCode === rcaInsuranceTypeCode), between(0, 100)] |> stopOnFirstFailure
          })
        )
      ] |> stopOnFirstFailure,
    periods: validatorPeriods,
    externalBeneficiary: required |> when(insuranceTypeCode === cascoInsuranceTypeCode && hasExternalBeneficiary === true)
  });
};

const InsuranceDataContainer = ({
  insuranceData,
  rcaInsuranceTypeCode,
  cascoInsuranceTypeCode,
  readOnly,
  dirtyInfoRef,
  setDirtyInfoRef
}) => {
  const showError = useSimpleError();
  const { t } = useTranslation();

  const [showActivityField, setShowActivityField] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [insuranceDataLens, insuranceDataDirtyInfo, resetInsuranceDataLens] = useChangeTrackingLens({});
  const [insurersLens, insurersLensDirtyInfo, resetInsurersLens] = useChangeTrackingLens([]);
  const [periodsLens, periodsLensDirtyInfo, resetPeriodsLens] = useChangeTrackingLens([]);
  const [insurerNames, setInsurerNames] = useState(emptyString);
  const [insuranceDataLoading, setInsuranceDataLoading] = useState(true);

  const { loading: financingTypesLoading, data: financingTypesData } = useQueryWithSimpleErrorHandling(FINANCING_TYPES_QUERY);

  const {
    loading: activityTypesLoading,
    dictionary: activityTypesData,
    getIdByCode: getActivityTypeIdByCode
  } = useDictionaryHandling(ACTIVITY_TYPES_QUERY, 'activityTypes');

  const { loading: insuranceTypesLoading, getCodeById: getInsuranceTypeCodeById } = useDictionaryHandling(
    INSURANCE_TYPES_QUERY,
    'insuranceTypes'
  );
  const insuranceTypeCode = useMemo(
    () => getInsuranceTypeCodeById(insuranceDataLens.insuranceTypeId |> get),
    [getInsuranceTypeCodeById, insuranceDataLens.insuranceTypeId]
  );

  const financingTypeConfigurationJSON = useParams('FinancingTypeConfiguration');
  const financingTypeConfiguration = useMemo(() => {
    if (financingTypeConfigurationJSON) return JSON.parse(financingTypeConfigurationJSON);
  }, [financingTypeConfigurationJSON]);

  const hasExternalBeneficiary = useMemo(() => {
    if (financingTypeConfiguration === undefined) return false;

    return (
      financingTypeConfiguration.find(x => x.FinancingTypeCode === (insuranceDataLens?.financingType |> get))?.HasExternalBeneficiary ===
        true && insuranceTypeCode === cascoInsuranceTypeCode
    );
  }, [financingTypeConfiguration, insuranceDataLens.financingType, insuranceTypeCode, cascoInsuranceTypeCode]);

  const handleFinancingTypeChange = useCallback(
    financingTypeValue => {
      (insuranceDataLens.financingType |> set)(financingTypeValue);
      const configuration = financingTypeConfiguration.find(x => x.FinancingTypeCode === financingTypeValue);

      if (!hasExternalBeneficiary) (insuranceDataLens.externalBeneficiary |> set)(null);
      const activityTypeVisible = configuration?.ActivityTypeVisibleYN;
      setShowActivityField(activityTypeVisible);
      if (activityTypeVisible) {
        const activityTypeCode = configuration?.DictionaryActivityField;
        const activityTypeId = getActivityTypeIdByCode(activityTypeCode);
        activityTypeId && (insuranceDataLens.activityTypeId |> set)(activityTypeId);
      } else {
        (insuranceDataLens.activityTypeId |> set)(null);
      }
    },
    [
      insuranceDataLens.financingType,
      insuranceDataLens.externalBeneficiary,
      hasExternalBeneficiary,
      insuranceDataLens.activityTypeId,
      financingTypeConfiguration,
      getActivityTypeIdByCode
    ]
  );

  const validatorInsuranceData = useMemo(
    () => getInsuranceDataValidator(insuranceTypeCode, cascoInsuranceTypeCode, rcaInsuranceTypeCode, hasExternalBeneficiary),
    [insuranceTypeCode, cascoInsuranceTypeCode, rcaInsuranceTypeCode, hasExternalBeneficiary]
  );
  const [validationInsuranceData, validateInsuranceData] = useDirtyFieldValidation(validatorInsuranceData);

  const { loading: currencyTypesLoading, data: currencyData } = useQueryWithSimpleErrorHandling(CURRENCY_QUERY);

  const { loading: monthNoValuesLoading, data: monthNoValuesData } = useQueryWithSimpleErrorHandling(MONTH_NO_VALUES_QUERY, {
    variables: { insuranceTypeCode },
    skip: !insuranceTypeCode
  });

  const getPeriods = useCallback(() => {
    let list = [];
    monthNoValuesData &&
      monthNoValuesData.monthNoValues &&
      monthNoValuesData.monthNoValues.length > 0 &&
      monthNoValuesData.monthNoValues.forEach(x => list.push({ id: x }));
    return list;
  }, [monthNoValuesData]);
  const periods = useMemo(() => getPeriods(), [getPeriods]);

  useEffect(() => {
    if (insuranceData) {
      resetInsuranceDataLens(insuranceData);
      setInsuranceDataLoading(false);
      setShowActivityField(Boolean(insuranceData.activityTypeId));
    }
  }, [insuranceData, resetInsuranceDataLens]);

  useEffect(() => {
    const insurers = insurersLens |> get;
    const periods = (periodsLens |> get).filter(period => period.selected);
    validateInsuranceData({ ...(insuranceDataLens |> get), insurers: insurers, periods: periods }, insuranceDataDirtyInfo);
  }, [insuranceDataLens, insurersLens, periodsLens, insuranceDataDirtyInfo, validateInsuranceData]);

  const getInsurersNames = useCallback(() => {
    const selectedInsurers = (insurersLens |> sequence).filter(insurer => insurer.selected |> get);
    const insurersNames = selectedInsurers.reduce((names, insurer, index) => {
      return `${names}${insurer.name |> get}${index < selectedInsurers.length - 1 ? ', ' : ''}`;
    }, emptyString);
    return insurersNames;
  }, [insurersLens]);

  useEffect(() => {
    setInsurerNames(getInsurersNames());
  }, [getInsurersNames]);

  useEffect(() => {
    const unsavedChanges = isDirty(insuranceDataDirtyInfo) || isDirty(insurersLensDirtyInfo) || isDirty(periodsLensDirtyInfo);
    setSaveDisabled(!unsavedChanges);

    setDirtyInfoRef('insuranceData', unsavedChanges);
    const { onDirtyInfoChange } = dirtyInfoRef.current;
    onDirtyInfoChange && onDirtyInfoChange();
  }, [insuranceDataDirtyInfo, insurersLensDirtyInfo, periodsLensDirtyInfo, dirtyInfoRef, setDirtyInfoRef]);

  const [saveInsuranceData] = useMutation(SAVE_INSURANCE_DATA, {
    onError: error => {
      setSaving(false);
      showError(error);
    }
  });

  useSubscription(INSURANCE_DOCUMENT_UPDATED, {
    variables: { documentId: insuranceDataLens.documentId |> get },
    skip: !(insuranceDataLens.documentId |> get),
    onData: () => {
      setSaving(false);
    }
  });

  const handleSave = () => {
    const insurers = insurersLens |> get;
    const periods = (periodsLens |> get).filter(period => period.selected);
    if (!validateInsuranceData({ ...(insuranceDataLens |> get), insurers: insurers, periods: periods })) return;

    let selectedInsurers = [];
    (insurersLens |> sequence).forEach(insurer => {
      if (insurer.selected |> get)
        selectedInsurers.push({
          insurerId: insurer.id |> get,
          requestedCommissionPercent: insurer.requestedCommissionPercent |> get
        });
    });

    let selectedPeriods = [];
    (periodsLens |> sequence).forEach(period => {
      if (period.selected |> get) selectedPeriods.push(period.id |> get);
    });

    setSaving(true);
    saveInsuranceData({
      variables: {
        input: {
          documentId: insuranceDataLens.documentId |> get,
          activityTypeId: insuranceDataLens.activityTypeId |> get,
          currencyId: insuranceDataLens.currencyId |> get,
          monthNo: insuranceDataLens.monthNo |> get,
          startDate: insuranceDataLens.startDate |> get,
          insuranceTypeCode: insuranceTypeCode,
          financingType: insuranceDataLens.financingType |> get,
          insurers: selectedInsurers,
          periods: selectedPeriods,
          externalBeneficiary: insuranceDataLens.externalBeneficiary |> get
        }
      }
    });
  };

  const loadingStatus = useMemo(() => {
    return {
      financingTypesLoading,
      insuranceTypesLoading,
      currencyTypesLoading,
      insuranceDataLoading,
      activityTypesLoading,
      monthNoValuesLoading,
      loadingCompleted: !(
        financingTypesLoading ||
        insuranceTypesLoading ||
        currencyTypesLoading ||
        insuranceDataLoading ||
        activityTypesLoading ||
        monthNoValuesLoading
      )
    };
  }, [
    financingTypesLoading,
    insuranceTypesLoading,
    currencyTypesLoading,
    insuranceDataLoading,
    activityTypesLoading,
    monthNoValuesLoading
  ]);

  const saveDisabledStatus = useMemo(() => {
    return saving || saveDisabled || !loadingStatus.loadingCompleted;
  }, [saving, saveDisabled, loadingStatus.loadingCompleted]);

  const summaryData = useMemo(() => {
    return {
      insurerNames,
      monthNo: insuranceDataLens?.monthNo |> get || emptyString,
      startDate: insuranceDataLens?.startDate |> get || emptyString
    };
  }, [insuranceDataLens.monthNo, insuranceDataLens.startDate, insurerNames]);

  return (
    <>
      <Prompt
        when={isDirty(insuranceDataDirtyInfo) || isDirty(insurersLensDirtyInfo) || isDirty(periodsLensDirtyInfo)}
        message={() => t('LeavingWithoutSaving')}
      />
      <InsuranceDataCard
        loadingStatus={loadingStatus}
        insuranceDataLens={insuranceDataLens}
        insurersLens={insurersLens}
        resetInsurersLens={resetInsurersLens}
        periodsLens={periodsLens}
        resetPeriodsLens={resetPeriodsLens}
        selectedInsurers={insuranceData.selectedInsurers}
        validationInsuranceData={validationInsuranceData}
        insuranceTypeCode={insuranceTypeCode}
        rcaInsuranceTypeCode={rcaInsuranceTypeCode}
        cascoInsuranceTypeCode={cascoInsuranceTypeCode}
        financingTypesData={financingTypesData?.financingTypes || emptyArray}
        activityTypesData={getActivityTypes(activityTypesData)}
        periods={periods}
        currencyData={currencyData}
        saving={saving}
        saveDisabled={saveDisabledStatus}
        selectedPeriods={insuranceData.selectedPeriods}
        onSave={handleSave}
        onFinancingTypeChange={handleFinancingTypeChange}
        showActivityField={showActivityField}
        summaryData={summaryData}
        readOnly={readOnly}
        hasExternalBeneficiary={hasExternalBeneficiary}
      />
    </>
  );
};

InsuranceDataContainer.propTypes = {
  insuranceData: PropTypes.object,
  rcaInsuranceTypeCode: PropTypes.string,
  cascoInsuranceTypeCode: PropTypes.string,
  readOnly: PropTypes.func.isRequired,
  dirtyInfoRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({ current: PropTypes.any })]).isRequired,
  setDirtyInfoRef: PropTypes.func.isRequired
};

export default InsuranceDataContainer;
